import { type AgeingAnalysisFilters } from "@/types/ageing-analysis";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface AdvancedFiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: AgeingAnalysisFilters;
  onFiltersChange: (filters: AgeingAnalysisFilters) => void;
  onApply: () => void;
  onReset: () => void;
}

export function AdvancedFiltersSheet({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  onApply,
  onReset,
}: AdvancedFiltersSheetProps) {
  const updateFilter = <K extends keyof AgeingAnalysisFilters>(key: K, value: AgeingAnalysisFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const toggleRiskLevel = (level: string) => {
    const newLevels = filters.riskLevel.includes(level)
      ? filters.riskLevel.filter(l => l !== level)
      : [...filters.riskLevel, level];
    updateFilter("riskLevel", newLevels);
  };

  const toggleStrategy = (strategy: string) => {
    const newStrategies = filters.collectionStrategy.includes(strategy)
      ? filters.collectionStrategy.filter(s => s !== strategy)
      : [...filters.collectionStrategy, strategy];
    updateFilter("collectionStrategy", newStrategies);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Advanced Filters</SheetTitle>
          <SheetDescription>
            Refine your customer list with advanced filtering options
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4 mt-6">
          <div className="space-y-6">
            {/* Risk Level */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Risk Level</Label>
              <div className="space-y-2">
                {["critical", "high", "medium", "low"].map((level) => (
                  <div key={level} className="flex items-center space-x-2">
                    <Checkbox
                      id={`risk-${level}`}
                      checked={filters.riskLevel.includes(level)}
                      onCheckedChange={() => toggleRiskLevel(level)}
                    />
                    <label
                      htmlFor={`risk-${level}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer capitalize"
                    >
                      {level}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Days Overdue Range */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Days Overdue Range</Label>
              <div className="space-y-4">
                <div className="px-2">
                  <Slider
                    min={0}
                    max={180}
                    step={10}
                    value={[
                      filters.daysOverdueMin ?? 0,
                      filters.daysOverdueMax ?? 180
                    ]}
                    onValueChange={([min, max]) => {
                      updateFilter("daysOverdueMin", min);
                      updateFilter("daysOverdueMax", max);
                    }}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{filters.daysOverdueMin ?? 0} days</span>
                  <span>{filters.daysOverdueMax ?? 180} days</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Outstanding Amount Range */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Outstanding Amount Range</Label>
              <div className="space-y-4">
                <div className="px-2">
                  <Slider
                    min={0}
                    max={500000}
                    step={10000}
                    value={[
                      filters.outstandingMin ?? 0,
                      filters.outstandingMax ?? 500000
                    ]}
                    onValueChange={([min, max]) => {
                      updateFilter("outstandingMin", min);
                      updateFilter("outstandingMax", max);
                    }}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{formatCurrency(filters.outstandingMin ?? 0)}</span>
                  <span>{formatCurrency(filters.outstandingMax ?? 500000)}</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Collection Strategy */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Collection Strategy</Label>
              <div className="space-y-2">
                {[
                  "Standard Collection Flow",
                  "Aggressive Collection",
                  "Gentle Reminder Flow",
                  "High-Value Customer",
                  "Legal Action Track"
                ].map((strategy) => (
                  <div key={strategy} className="flex items-center space-x-2">
                    <Checkbox
                      id={`strategy-${strategy}`}
                      checked={filters.collectionStrategy.includes(strategy)}
                      onCheckedChange={() => toggleStrategy(strategy)}
                    />
                    <label
                      htmlFor={`strategy-${strategy}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {strategy}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Has Open Disputes */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Has Open Disputes</Label>
              <Select
                value={
                  filters.hasDisputes === null
                    ? "all"
                    : filters.hasDisputes
                    ? "yes"
                    : "no"
                }
                onValueChange={(value) => {
                  updateFilter(
                    "hasDisputes",
                    value === "all" ? null : value === "yes"
                  );
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Has Promises to Pay */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Has Promises to Pay</Label>
              <Select
                value={
                  filters.hasPromisesToPay === null
                    ? "all"
                    : filters.hasPromisesToPay
                    ? "yes"
                    : "no"
                }
                onValueChange={(value) => {
                  updateFilter(
                    "hasPromisesToPay",
                    value === "all" ? null : value === "yes"
                  );
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={onReset}>
            Reset
          </Button>
          <Button onClick={onApply}>Apply Filters</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
